// Providers + head plumbing, ported from eb-ui src/index.tsx + server.tsx bootstrap.
//   - IntlProvider (react-intl) for the consumer chrome (global header / footer);
//   - QueryClientProvider (@tanstack/react-query) for header data fetching;
//   - ConsentManagerScripts (webapp-data) — paired with the Transcend <Script> in _document;
//   - FavIconLinks / MetaTags / AssetsPrefetchLinks (webapp-public-content);
//   - neue-plak.css: the Neue Plak brand webfont @font-face (base64 WOFF). REQUIRED — eds.scss
//     only *references* `font-family: 'Neue Plak'`; nothing else loads the face, so without
//     this the page silently falls back to the system font (SF Pro) and renders ~110% larger.
//     (webapp-fonts is NOT this — it's only the `?be_grotesk=true` Founders-Grotesk QA switch.)
//   - our global EDS/utility stylesheet;
//   - setGettextMessages: hydrate the plain-string gettext catalog for the trending strings.

import '@/styles/neue-plak.css';
import '@/styles/eds.scss';
import '@eventbrite/webapp-fonts/fonts.css';

// Deep-subpath import: the `@eventbrite/webapp-constants@1.0.1` barrel
// (`dist/index.d.ts`) re-exports from a non-existent `lib/` path, so its types
// don't resolve; the `dist/*` modules are intact.
import { Stage, stageFromString } from '@eventbrite/webapp-constants/dist/Stage';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { AssetsPrefetchLinks, FavIconLinks, MetaTags } from '@eventbrite/webapp-public-content';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { useMemo } from 'react';
import { IntlProvider } from 'react-intl';

import { setGettextMessages } from '@/i18n/gettext';
import { formatLocale, getLocaleMessages } from '../../i18n/locale';

interface TrendingWebappProps extends AppProps {
    stage: Stage;
    locale: string;
    messages: Record<string, string>;
}

export default function App({
    Component,
    pageProps,
    stage,
    locale,
    messages,
}: TrendingWebappProps) {
    const queryClient = useMemo(() => new QueryClient(), []);

    // Populate the plain-string gettext catalog used by the ported trending strings.
    // Idempotent + synchronous, so it is set before children render on server & client.
    setGettextMessages(messages);

    return (
        <>
            <Head>
                <FavIconLinks stage={stage} />
            </Head>
            <MetaTags />
            <AssetsPrefetchLinks />
            <QueryClientProvider client={queryClient}>
                <IntlProvider locale={locale} defaultLocale="en-US" messages={messages}>
                    <Component {...pageProps} />
                </IntlProvider>
            </QueryClientProvider>
            <ConsentManagerScripts />
        </>
    );
}

App.getInitialProps = async (context: AppContext) => {
    const appProps = await NextApp.getInitialProps(context);
    const stage = stageFromString(process.env.STAGE || Stage.TEST);
    const req = context.ctx.req;
    const localeUnderscore = req ? getLocaleFromRequest(req) : 'en_US';
    const messages = await getLocaleMessages(localeUnderscore);
    return {
        ...appProps,
        stage,
        locale: formatLocale(localeUnderscore),
        messages,
    };
};
